import { useTranslation } from "react-i18next";
import type { DirectionalBias } from "../api/types";
import { Badge } from "@/components/ui/badge";
import { biasTone } from "./format";
import { cn } from "@/lib/utils";

export type DirectionTone = "bull" | "bear" | "neutral";

/**
 * The structured direction enum the collector emits as
 * `key_drivers[].direction`. Mirrors enums.json[direction].
 */
export type DirectionEnum = "up" | "down" | "sideways" | "unknown";

/**
 * `directionTone` chooses the dashboard colour for a key-driver direction.
 *
 * IMPORTANT (C4):
 * Mirrors `biasTone`. Accepts either:
 *
 *   1. A structured `{ direction }` (preferred) — the backend's enum code,
 *      language-independent. Routed by switch.
 *   2. A bare string (legacy callers) — substring sniff for English
 *      bullish/bearish/up/down/higher/lower keywords. TODO-marked for
 *      removal once the API ships the enum on every driver.
 *
 * The fallback branch stays for now because `KeyDriver.direction` is still
 * a free-form `string` in `api/types.ts` and several callers concatenate
 * the driver label into it (see `AnalysisExpansion.tsx:98`).
 */
export type DirectionToneInput = string | { direction?: DirectionEnum | string };

export function directionTone(input: DirectionToneInput): DirectionTone {
  if (typeof input === "object" && input !== null) {
    const code = input.direction;
    if (code) {
      switch (code) {
        case "up":
          return "bull";
        case "down":
          return "bear";
        case "sideways":
        case "unknown":
          return "neutral";
      }
      // Unrecognized enum — sniff as a label string.
      return sniffDirection(code);
    }
    return "neutral";
  }
  return sniffDirection(input);
}

function sniffDirection(value: string): DirectionTone {
  const v = value.toLowerCase();
  if (v.includes("bear") || v.includes("down") || v.includes("lower")) return "bear";
  if (v.includes("bull") || v.includes("up") || v.includes("higher")) return "bull";
  return "neutral";
}

export function toneClass(tone: DirectionTone): string {
  if (tone === "bull") return "bg-emerald-500/15 text-emerald-500 border-emerald-500/30";
  if (tone === "bear") return "bg-destructive/15 text-destructive border-destructive/30";
  return "bg-secondary text-secondary-foreground border-border";
}

export function toneTextClass(tone: DirectionTone): string {
  if (tone === "bull") return "text-emerald-500";
  if (tone === "bear") return "text-destructive";
  return "text-muted-foreground";
}

export function BiasBadge({ bias }: { bias: DirectionalBias | undefined }) {
  const { t } = useTranslation("dashboard");
  if (!bias) {
    return (
      <Badge variant="secondary" className="text-xs">
        {t("bias.none")}
      </Badge>
    );
  }
  // Structured enum first, label sniff second — language-safe.
  const tone = biasTone({ bias: bias.bias, label: bias.label });
  return (
    <Badge
      className={cn("text-xs font-medium border", toneClass(tone))}
      title={t("bias.horizonTitle", { value: bias.horizon })}
    >
      {bias.label}
    </Badge>
  );
}

export function ConvictionBar({
  value,
  showLabel = true,
}: {
  value: number;
  showLabel?: boolean;
}) {
  const { t } = useTranslation("dashboard");
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="flex items-center gap-2">
      {showLabel && (
        <div className="text-xs text-muted-foreground">
          {t("bias.convictionLabel", { value: pct })}
        </div>
      )}
      <div
        className="relative h-1.5 w-20 overflow-hidden rounded-full bg-muted"
        aria-label={t("bias.convictionAria", { value: pct })}
      >
        <span
          className="block h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
